import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

interface BulkDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (dates: string[]) => void;
  month: string;
  year: number;
  maxOptions?: number;
}

export function BulkDateModal({ 
  isOpen, 
  onClose, 
  onSave, 
  maxOptions = 300 
}: BulkDateModalProps) {
  const [options, setOptions] = useState('');

  if (!isOpen) return null;

  const handleSave = () => {
    const dateStrings = options
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
    
    onSave(dateStrings);
    setOptions('');
    onClose();
  };

  const handleCancel = () => {
    setOptions('');
    onClose();
  };

  const optionCount = options.split('\n').filter(line => line.trim().length > 0).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Bulk edit options</h2>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            Add dates in any format: full dates (01/05/2024, 01-05-2024, 2024-01-05, 01052024) or ordinal dates (5th, 10th). For ordinal dates, month/year will be inferred from existing dates or current date. Separate each on a new line.
          </p>
          
          <div className="space-y-2">
            <Label htmlFor="options">Options</Label>
            <div className="relative">
              <Textarea
                id="options"
                value={options}
                onChange={(e) => setOptions(e.target.value)}
                placeholder="5th&#10;10th&#10;01052024&#10;01/05/2024&#10;01-05-2024"
                className="min-h-[120px] resize-none"
                maxLength={maxOptions * 10} // Rough estimate for character limit
              />
              <div className="absolute top-2 right-2 text-xs text-gray-500 bg-white px-1 rounded">
                {optionCount} of {maxOptions} options
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="px-4 py-2"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white"
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
